import { useSearchFilters } from '../../../common/components/SearchHeader/filters/useSearchFilters';
import {
  FilterChoice,
  SPECIALTY_GENERAL_PRACTICE,
} from '../../../common/components/SearchHeader/types/FilterChoice';
import { flatMapObject } from '../../../common/util/flatMapObject';
import { useOverlay } from '../overlay/OverlayContext';

export const useResetFilters = () => {
  const [filters, setFilters] = useSearchFilters();
  const { clear } = useOverlay();

  const filterMask = getFilterMask(filters);

  return () => {
    setFilters({ ...filters, ...filterMask });
    clear();
  };
};

export const useNoFilter = () => {
  const [filters] = useSearchFilters();
  const filterMask = getFilterMask(filters);

  return Object.keys(filterMask).length === 0;
};

/** Returns a mapping of the changes which should be applied by resetting the filters.
 * If there are no changes to apply, this mapping will be empty.
 */
const getFilterMask = (filters: FilterChoice) =>
  flatMapObject(filters, entry => {
    const { alwaysShowFilters } = filters;

    // /** These filters should never be changed by resetting. */
    if (entry.key === 'text') return [];

    /** When some filters are hidden, location should never be reset. */
    if (!alwaysShowFilters && entry.key === 'location') return [];

    /** When some filters are hidden, the behaviour of topic depends on its value. */
    if (!alwaysShowFilters && entry.key === 'topic') {
      /** Bulk-billing GP should reset to ordinary GP. Other topics should not reset at all. */
      return entry.value?.bulkBillingGp
        ? [{ key: 'topic', value: { specialist: SPECIALTY_GENERAL_PRACTICE } }]
        : [];
    }

    /** If the filter does not have a truthy value, it does not need to be reset. */
    if (!entry.value) return [];

    /** If the filter does not have properties in the object, it does not need to be reset. */
    if (entry.value instanceof Object && Object.keys(entry.value).length === 0) return [];

    /** All other filters get reset to undefined. */
    return [{ key: entry.key, value: undefined }];
  });
